import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, CheckCircle } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { money, fdate } from "@/lib/format";
import { PageHeader, EmptyState, StatusBadge, StatCard } from "@/components/common";
import { TextField, SelectField } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CONCEPTOS = [["administracion", "Administración"], ["colocacion", "Colocación"], ["renovacion", "Renovación"], ["mantenimiento", "Mantenimiento"], ["penalizacion", "Penalización"], ["otro", "Otro"]].map(([value, label]) => ({ value, label }));
const ESTADOS = [["generada", "Generada"], ["pendiente_cobro", "Pendiente de cobro"], ["cobrada", "Cobrada"], ["cancelada", "Cancelada"]].map(([value, label]) => ({ value, label }));

export default function Commissions() {
  const [items, setItems] = useState([]);
  const [owners, setOwners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ concepto: "colocacion", owner_id: "", importe: "", estado: "generada", nota: "" });

  const load = () => { api.get("/commissions").then((r) => setItems(r.data)); api.get("/owners").then((r) => setOwners(r.data)); };
  useEffect(() => { load(); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.importe || form.importe <= 0) return toast.error("Importe inválido");
    try {
      await api.post("/commissions", { ...form, importe: parseFloat(form.importe), owner_id: form.owner_id || null });
      toast.success("Comisión registrada"); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setEstado = async (id, estado) => { await api.put(`/commissions/${id}/estado`, null, { params: { estado } }); toast.success("Actualizada"); load(); };
  const del = async (id) => { if (!window.confirm("¿Eliminar comisión?")) return; await api.delete(`/commissions/${id}`); toast.success("Eliminada"); load(); };

  const generadas = items.reduce((s, i) => s + (i.importe || 0), 0);
  const cobradas = items.filter((i) => i.estado === "cobrada").reduce((s, i) => s + (i.importe || 0), 0);
  const pendientes = items.filter((i) => i.estado !== "cobrada" && i.estado !== "cancelada").reduce((s, i) => s + (i.importe || 0), 0);

  return (
    <div data-testid="commissions-page">
      <PageHeader title="Comisiones de la inmobiliaria" subtitle="El corazón del modelo de negocio" actions={<Button data-testid="new-commission-btn" onClick={() => { setForm({ concepto: "colocacion", owner_id: "", importe: "", estado: "generada", nota: "" }); setOpen(true); }} className="rounded-sm gap-2"><Plus size={16} /> Nueva comisión</Button>} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard label="Comisiones generadas" value={money(generadas)} />
        <StatCard label="Comisiones cobradas" value={money(cobradas)} tone="accent" />
        <StatCard label="Comisiones pendientes" value={money(pendientes)} tone="warn" />
      </div>

      {items.length === 0 ? (
        <EmptyState title="Sin comisiones" hint="Las comisiones por administración se generan al cobrar rentas. Registra otras aquí." />
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Concepto</TableHead><TableHead>Propietario</TableHead><TableHead>Inmueble</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Importe</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`commission-row-${c.id}`}>
                  <TableCell className="font-medium capitalize">{c.concepto}</TableCell>
                  <TableCell>{c.owner_nombre || "—"}</TableCell>
                  <TableCell>{c.property_nombre || "—"}</TableCell>
                  <TableCell className="text-xs">{fdate(c.fecha)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold text-primary">{money(c.importe)}</TableCell>
                  <TableCell><StatusBadge estado={c.estado} /></TableCell>
                  <TableCell className="text-right">
                    {c.estado !== "cobrada" && c.estado !== "cancelada" && (
                      <Button variant="ghost" size="icon" className="rounded-sm text-emerald-600" data-testid={`cobrar-commission-${c.id}`} onClick={() => setEstado(c.id, "cobrada")} title="Marcar cobrada"><CheckCircle size={16} /></Button>
                    )}
                    <Button variant="ghost" size="icon" className="rounded-sm text-red-600" onClick={() => del(c.id)}><Trash size={16} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Nueva comisión</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <SelectField label="Concepto" value={form.concepto} onChange={set("concepto")} testId="commission-concepto" options={CONCEPTOS} />
            <SelectField label="Propietario" value={form.owner_id} onChange={set("owner_id")} options={owners.map((o) => ({ value: o.id, label: o.nombre }))} />
            <TextField label="Importe" type="number" value={form.importe} onChange={set("importe")} testId="commission-importe" />
            <SelectField label="Estado" value={form.estado} onChange={set("estado")} options={ESTADOS} />
            <TextField label="Nota" value={form.nota} onChange={set("nota")} />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm" data-testid="save-commission-btn" onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
